import { ReactElement, ReactNode } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  GQL_MUTATION_UPDATE_COMPANY,
  GQL_MUTATION_UPDATE_ME,
} from '../utility/queries';
import { AccountDetailsForm } from './AccountDetailsForm';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';
import { track } from '../utility/analytics';

export const AccountDetailsGate = ({
  description,
  children,
}: {
  description: string;
  children: ReactNode;
}): ReactElement => {
  const { user } = useUser();

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const [updateMe, { loading: isSavingUser }] = useMutation(
    GQL_MUTATION_UPDATE_ME,
    { onError }
  );

  const [updateCompany, { loading: isSavingCompany }] = useMutation(
    GQL_MUTATION_UPDATE_COMPANY,
    { onError }
  );

  if (user.name && user.company.name) return <>{children}</>;

  const isSaving = isSavingUser || isSavingCompany;

  return (
    <div className="p-5 space-y-5">
      <p className="text-text-low-priority">{description}</p>

      <AccountDetailsForm
        loading={isSaving}
        defaultValues={{
          fullName: user.name ?? '',
          companyName: user.company.name ?? '',
        }}
        onSubmit={async ({ fullName, companyName }) => {
          // `onError` makes a failed mutation resolve, so check the results.
          const [userResult, companyResult] = await Promise.all([
            user.name === fullName
              ? null
              : updateMe({
                  variables: { input: { name: fullName, email: null } },
                }),
            user.company.name === companyName
              ? null
              : updateCompany({ variables: { input: { name: companyName } } }),
          ]);

          if (userResult?.error || companyResult?.error) return;

          track('Account Details Submitted');
        }}
      />
    </div>
  );
};
